import { Component, OnDestroy, OnInit } from '@angular/core';
import { AiWeltMessage, AiWeltService, AiWeltThread } from '../../../_services/dispo/ai-welt.service';
import { AiWeltPresenceTrackerService } from '../../../_services/dispo/ai-welt-presence-tracker.service';

/** Floating Intern-Chat für Mitarbeiter (Antworten auf Root-Nachrichten). */
@Component({
  selector: 'app-ai-welt-inbox',
  template: `
    <div class="aiw-fab" *ngIf="visible" (click)="toggle()">
      <span class="icon">💬</span>
      <span class="badge" *ngIf="tracker.unreadCount > 0">{{ tracker.unreadCount }}</span>
    </div>
    <div class="aiw-panel" *ngIf="open">
      <header>
        <strong>Interner Chat</strong>
        <button type="button" (click)="open = false">×</button>
      </header>
      <div class="msgs">
        <div *ngIf="!messages.length" class="empty">Keine Nachrichten</div>
        <div *ngFor="let m of messages" class="m" [class.mine]="isMine(m)">
          {{ m.body }}
        </div>
      </div>
      <footer>
        <input [(ngModel)]="draft" placeholder="Antwort…" (keydown.enter)="send()" />
        <button type="button" (click)="send()" [disabled]="!draft.trim() || sending">OK</button>
      </footer>
    </div>
  `,
  styles: [`
    .aiw-fab {
      position: fixed; right: 18px; bottom: 18px; z-index: 12000;
      width: 48px; height: 48px; border-radius: 50%;
      background: #1f2a33; color: #fff; display: flex; align-items: center; justify-content: center;
      cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,.25);
    }
    .aiw-fab .badge {
      position: absolute; top: -4px; right: -4px; background: #e74c3c; color: #fff;
      font-size: 11px; min-width: 18px; height: 18px; border-radius: 9px;
      display: flex; align-items: center; justify-content: center; padding: 0 4px;
    }
    .aiw-panel {
      position: fixed; right: 18px; bottom: 78px; z-index: 12000;
      width: 320px; max-height: 420px; background: #f7f4ef; border: 1px solid #cfc6b8;
      border-radius: 10px; display: flex; flex-direction: column; box-shadow: 0 8px 24px rgba(0,0,0,.2);
    }
    .aiw-panel header {
      display: flex; justify-content: space-between; align-items: center;
      padding: 10px 12px; background: #efe8dc; border-bottom: 1px solid #ddd;
    }
    .aiw-panel header button { border: 0; background: transparent; font-size: 18px; cursor: pointer; }
    .msgs { flex: 1; overflow: auto; padding: 10px; display: flex; flex-direction: column; gap: 6px; min-height: 160px; }
    .msgs .empty { color: #889; font-size: 12px; margin: auto; }
    .m { background: #fff; border: 1px solid #e2d9cc; border-radius: 8px; padding: 6px 8px; font-size: 13px; max-width: 90%; align-self: flex-start; white-space: pre-wrap; }
    .m.mine { align-self: flex-end; background: #dceaf7; }
    footer { display: flex; gap: 6px; padding: 8px; border-top: 1px solid #ddd; }
    footer input { flex: 1; border: 1px solid #ccc; border-radius: 6px; padding: 6px 8px; }
    footer button { border: 0; background: #2b6cb0; color: #fff; border-radius: 6px; padding: 6px 10px; cursor: pointer; }
  `],
})
export class AiWeltInboxComponent implements OnInit, OnDestroy {
  open = false;
  visible = false;
  draft = '';
  sending = false;
  thread: AiWeltThread | null = null;
  messages: AiWeltMessage[] = [];
  private meId: number | null = null;
  private poll: any;

  constructor(
    public tracker: AiWeltPresenceTrackerService,
    private aiWelt: AiWeltService
  ) {}

  ngOnInit(): void {
    const user = this.readUser();
    if (!user || user.role === 'Root') {
      this.visible = false;
      return;
    }
    this.meId = user.id || user.user_id || null;
    this.visible = true;
    this.poll = setInterval(() => {
      if (this.open && this.tracker.unreadCount > 0) {
        this.load();
      }
    }, 15000);
  }

  ngOnDestroy(): void {
    if (this.poll) {
      clearInterval(this.poll);
    }
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.load();
    }
  }

  isMine(m: AiWeltMessage): boolean {
    return !!this.meId && m.sender_id === this.meId;
  }

  private readUser(): any {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }

  private load(): void {
    this.aiWelt.getThreads({ type: 'internal' }).subscribe({
      next: (res) => {
        const t = (res.threads || [])[0];
        this.thread = t || null;
        if (!t) {
          this.messages = [];
          return;
        }
        this.aiWelt.getThread(t.id).subscribe({
          next: (full) => {
            this.thread = full.thread;
            this.messages = full.messages || [];
          },
        });
      },
    });
  }

  send(): void {
    const text = (this.draft || '').trim();
    if (!text || this.sending) {
      return;
    }
    this.sending = true;
    this.aiWelt
      .sendMessage({
        type: 'internal',
        thread_id: this.thread?.id,
        message: text,
      })
      .subscribe({
        next: (res) => {
          this.thread = res.thread;
          this.messages = [...this.messages, ...(res.messages || [])];
          this.draft = '';
          this.sending = false;
        },
        error: () => {
          this.sending = false;
        },
      });
  }
}
